import { FormControl, FormGroup, Validators } from '@angular/forms';
import { uniqueValueValidator, Unsubscriber } from '@core';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalRef } from '@core-lib/modules/modal-container';
import { Store } from '@ngrx/store';
import { PreferenceService } from '@core-lib/services/preference.service';
import { IEditPreferences, IPreference, IReleveAffaireForm, ReleveAffaire } from '@core-lib/models/preference.model';
import { Preference } from '@core-lib/enums/preference.enum';

@Component({
    selector: 'settings-releve-affaire',
    templateUrl: './settings-releve-affaire.component.html',
    styleUrls: ['./settings-releve-affaire.component.scss'],
    standalone: false
})
export class SettingReleveAffaireComponent extends Unsubscriber implements OnInit, OnDestroy {
	@Input() structureId: string;
	@Input() allowEdit: boolean;
	@Input() preferenceReleveAffaires$: Observable<ReleveAffaire> = new Observable<ReleveAffaire>();

	modalRef: ModalRef;
	form: FormGroup;
	isEmail1Required = false;

	preferenceReleveAffaires: ReleveAffaire | null = null;
	anyEditMode$: Observable<boolean> = this.preferenceService.anyEditMode$;

	get isEditMode() {
		return this.preferenceService.releveAffaireEditMode$.value;
	}

	constructor(
		public preferenceService: PreferenceService,
		protected store$: Store,
		private toastr: ToastrService,
		private translate: TranslateService
	) {
		super();
	}

	ngOnInit(): void {
		this.preferenceReleveAffaires$.subscribe(preferenceReleveAffaires => {
			this.preferenceReleveAffaires = preferenceReleveAffaires;
			this.initForm();
		});
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	private initForm(): void {
		this.preferenceService.releveAffaireEditMode$.next(false);
		this.form = new FormGroup<IReleveAffaireForm>({
			envoieAutomatique: new FormControl(this.preferenceReleveAffaires.envoieAutomatique.valeur === 'true'),
			email1: new FormControl(this.preferenceReleveAffaires.email1.valeur, [
				Validators.email,
				uniqueValueValidator(['email2', 'email3'])
			]),
			email2: new FormControl(this.preferenceReleveAffaires.email2.valeur, [
				Validators.email,
				uniqueValueValidator(['email1', 'email3'])
			]),
			email3: new FormControl(this.preferenceReleveAffaires.email3.valeur, [
				Validators.email,
				uniqueValueValidator(['email1', 'email2'])
			])
		});
		// Ajoutez un observateur sur le champ envoieAutomatique
		this.form.get('envoieAutomatique').valueChanges.subscribe(value => {
			this.updateEmailRequiredState();
		});
		this.updateEmailRequiredState();
	}

	private updateEmailRequiredState(): void {
		const email1Ctrl = this.form.get('email1') as FormControl;

		this.isEmail1Required = this.form.get('envoieAutomatique')?.value;
		if (this.isEmail1Required) {
			email1Ctrl.setValidators([Validators.required, Validators.email]);
		} else {
			email1Ctrl.setValidators([Validators.email]);
		}

		email1Ctrl.updateValueAndValidity();
	}

	edit(): void {
		this.preferenceService.releveAffaireEditMode$.next(true);
	}

	cancel(): void {
		this.preferenceService.releveAffaireEditMode$.next(false);
		this.initForm();
	}

	confirm(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		var preferences = [
			<IPreference>{
				actif: true,
				code: Preference.releveaffaires.email1.code,
				valeur: this.form.value.email1
			},
			<IPreference>{
				actif: true,
				code: Preference.releveaffaires.email2.code,
				valeur: this.form.value.email2
			},
			<IPreference>{
				actif: true,
				code: Preference.releveaffaires.email3.code,
				valeur: this.form.value.email3
			},
			<IPreference>{
				actif: true,
				code: Preference.releveaffaires.envoieAutomatique.code,
				valeur: this.form.value.envoieAutomatique.toString()
			}
		];

		const editPreferences: IEditPreferences = {
			category: Preference.category.releveaffaires,
			preferences: preferences
		};

		this.preferenceService.edit(this.structureId, editPreferences).subscribe({
			next: () => {
				this.translate.get('Success.Save').subscribe(msg => this.toastr.success(msg));
				this.preferenceService.notificationsEditMode$.next(false);
				this.preferenceService.get(this.structureId, true);
			},
			error: error => {
				this.toastr.error(error);
			}
		});
	}
}
